import { useEffect, useState } from 'react';
import type { CycleStateDto } from '@genoma/shared';

import { useI18n } from '../i18n/index.tsx';
import { fetchCycleState, setCycleStatus } from '../services/dashboard.service.ts';
import { useCan } from '../stores/auth.store.ts';

/**
 * Whether anybody can answer, and the switch that decides it.
 *
 * It lives in the rail rather than on a settings page because its state is
 * something you need to know while doing something else: a closed cycle makes
 * every invitation pointless and every "why is nobody answering" the wrong
 * question. So it is visible to everyone who can see progress, and movable by
 * an admin.
 *
 * Both directions are consequential and neither is undone by clicking again.
 * Opening starts the collection and freezes the 360 map. Closing takes the
 * questionnaire away from whoever is halfway through one. So the button asks,
 * and the question names what is at stake — how many people, how many answers —
 * rather than being a bare "are you sure".
 */
export const CycleSwitch = () => {
  const { t } = useI18n();
  const canOpen = useCan('cycle:open');

  const [cycle, setCycle] = useState<CycleStateDto | null>(null);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCycleState()
      .then(setCycle)
      .catch(() => setCycle(null));
  }, []);

  if (!cycle) return null;

  const open = cycle.status === 'OPEN';
  const archived = cycle.status === 'ARCHIVED';

  const flip = async () => {
    setBusy(true);
    setError(null);
    try {
      setCycle(await setCycleStatus(open ? 'CLOSED' : 'OPEN'));
      setAsking(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 rounded-xl border border-white/10 bg-white/5 px-3 py-2.5">
      <p className="flex items-center gap-2 font-mono text-[9.5px] tracking-[0.12em] text-white/40 uppercase">
        {/* The dot is never the only signal: the word next to it says the same
            thing, for anyone who cannot tell the two colours apart. */}
        <span
          aria-hidden
          className={[
            'size-2 rounded-full',
            open ? 'bg-[var(--color-rail-bright)]' : 'bg-white/30',
          ].join(' ')}
        />
        {t('cycle.label')}
      </p>

      <p className="mt-1 text-[13px] font-semibold text-white/90">
        {archived
          ? t('cycle.archived')
          : open
            ? t('cycle.statusOpen')
            : t('cycle.statusClosed')}
      </p>

      <p className="mt-0.5 text-[11.5px] leading-snug text-white/40">
        {open
          ? t('cycle.openHelp', { submitted: cycle.submitted, raters: cycle.raters })
          : t('cycle.closedHelp')}
      </p>

      {canOpen && !archived && (
        <>
          {asking ? (
            <div className="mt-2.5">
              <p className="text-[11.5px] leading-snug text-white/70">
                {open
                  ? t('cycle.confirmClose', {
                      pending: cycle.raters - cycle.submitted,
                      responses: cycle.responses,
                    })
                  : t('cycle.confirmOpen', { raters: cycle.raters })}
              </p>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void flip()}
                  className={[
                    'cursor-pointer rounded-lg px-2.5 py-1.5 font-mono text-[10px] tracking-[0.08em] uppercase disabled:opacity-50',
                    open
                      ? 'bg-[var(--color-critical)] text-white'
                      : 'bg-[var(--color-rail-bright)] text-[#10262a]',
                  ].join(' ')}
                >
                  {busy ? t('cycle.working') : open ? t('cycle.doClose') : t('cycle.doOpen')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAsking(false);
                    setError(null);
                  }}
                  className="cursor-pointer border-none bg-transparent px-1 font-mono text-[10px] tracking-[0.08em] text-white/50 uppercase"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setAsking(true)}
              className="mt-2 w-full cursor-pointer rounded-lg border border-white/15 bg-transparent px-2.5 py-1.5 font-mono text-[10px] tracking-[0.08em] text-white/70 uppercase transition hover:border-white/35 hover:text-white"
            >
              {open ? t('cycle.close') : t('cycle.open')}
            </button>
          )}

          {error && <p className="mt-2 text-[11.5px] text-[#f0a3a3]">{error}</p>}
        </>
      )}
    </div>
  );
};
